<script setup lang="ts">
import type { SuggestionType } from '@apps/main/new-desktop/modules/suggestionTypeSelector';
import {
  ItinerarySegmentPlaceholderType,
  ItinerarySegmentPlaceholder,
} from '@apps/main/new-desktop/modules/itineraryButton';

import { TransportType } from '@libs/trip-planner-interfaces';

import ItineraryTransportSelector from '../ItineraryTransportSelector.vue';
import type { UnavailableItineraryFerrySegment } from '../../../models';
import ItineraryTimeline from '../ItineraryTimeline.vue';

import FerrySegmentHeader from './FerrySegmentHeader.vue';
import UnavailableFerryProduct from './UnavailableFerryProduct.vue';

interface UnavailableFerrySegmentProps {
  segment: UnavailableItineraryFerrySegment;
  unavailabilityMessage: string;
  shouldHideProductLink: boolean;
}

const props = defineProps<UnavailableFerrySegmentProps>();

interface UnavailableFerrySegmentEmits {
  (eventName: 'edit-filters'): void;
  (eventName: 'remove-filter', tagId: string): void;
  (eventName: 'select-transport', transportType: TransportType): void;
  (eventName: 'show-more-filters'): void;
  (eventName: 'suggestion-type-selected', suggestionType: SuggestionType): void;
  (eventName: 'try-another-date'): void;
  (eventName: 'remove-ferry'): void;
}

const emit = defineEmits<UnavailableFerrySegmentEmits>();
</script>

<template>
  <ItineraryTimeline
    :segment-type="props.segment.segmentType"
    class="itinerary-ferry-segment"
  >
    <template #dropdown>
      <ItineraryTransportSelector
        v-if="!props.shouldHideProductLink"
        :transport-type="TransportType.Ferry"
        :disabled="false"
        @select-transport="emit('select-transport', $event)"
      />
      <ItinerarySegmentPlaceholder
        v-else
        :transport-type="ItinerarySegmentPlaceholderType.Ferry"
      />
    </template>
    <template #header>
      <FerrySegmentHeader
        :segment="props.segment"
        :print-mode="false"
        :should-hide-product-link="props.shouldHideProductLink"
        @show-more-filters="emit('show-more-filters')"
        @remove-filter="emit('remove-filter', $event)"
        @suggestion-type-selected="emit('suggestion-type-selected', $event)"
        @remove-ferry="emit('remove-ferry')"
      />
    </template>

    <template #content>
      <UnavailableFerryProduct
        :reasons="props.segment.reasons"
        :message="props.unavailabilityMessage"
        @edit-filters="emit('edit-filters')"
        @try-another-date="emit('try-another-date')"
      />
    </template>
  </ItineraryTimeline>
</template>
